import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { systemReadiness } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, ShieldCheck, Warning, XCircle } from "@phosphor-icons/react";

const DOT = { green: "bg-emerald-400", yellow: "bg-amber-400", red: "bg-red-500" };
const TXT = { green: "text-emerald-400", yellow: "text-amber-400", red: "text-red-400" };

export default function SystemReadinessPage() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const { data: d, isFetching } = useQuery({
    queryKey: ["system-readiness", activeId],
    queryFn: () => systemReadiness(activeId, false),
    enabled: !!activeId,
  });
  const recheck = async () => {
    await systemReadiness(activeId, true);
    qc.invalidateQueries({ queryKey: ["system-readiness", activeId] });
  };

  const overall = d?.overall;
  const banner = overall === "green"
    ? { Icon: ShieldCheck, cls: "border-emerald-500/25 bg-emerald-500/[0.06] text-emerald-300", msg: "All systems go — Vozelto is ready for production." }
    : overall === "yellow"
      ? { Icon: Warning, cls: "border-amber-500/25 bg-amber-500/[0.06] text-amber-300", msg: "Launch-ready with warnings — optional items below are not connected yet." }
      : { Icon: XCircle, cls: "border-red-500/25 bg-red-500/[0.06] text-red-300", msg: "Blocking issues found — fix the red items before launching." };

  return (
    <PageContainer>
      <PageHeader eyebrow="Version 1.0" title="System Readiness"
        subtitle="Final pre-launch diagnostic — every core system verified live: connections, keys, scheduler, database, storage and webhooks.">
        <Button data-testid="readiness-recheck-btn" variant="outline" onClick={recheck} disabled={isFetching}
          className="border-white/15 bg-transparent hover:bg-white/5 text-white">
          <ArrowsClockwise size={16} className={`mr-1.5 ${isFetching ? "animate-spin" : ""}`} /> Run diagnostics
        </Button>
      </PageHeader>

      {d && (
        <div className="card-matte p-6 mb-5 brand-glow" data-testid="launch-score-card">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="font-head text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="launch-score">
                🚀 Launch Readiness: <span className="brand-text">{d.score}%</span>
              </div>
              <div className="text-sm text-[#9a9a9a] mt-1">🟢 {d.greens} passing · 🟡 {d.yellows} pending · 🔴 {d.reds} failing</div>
            </div>
            <div className="h-2 w-full sm:w-64 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#6B3DFF]" style={{ width: `${d.score}%` }} />
            </div>
          </div>
          {(d.pending || []).length > 0 && (
            <div className="mt-4 border-t border-white/8 pt-3" data-testid="launch-pending-list">
              <div className="label-caps mb-2">Preventing 100%</div>
              <div className="space-y-1.5">
                {d.pending.map((p) => (
                  <div key={p.name} className="text-sm flex items-start gap-2" data-testid="launch-pending-item">
                    <span>{p.color === "red" ? "❌" : "⏳"}</span>
                    <span className="text-[#cfcfcf] font-semibold">{p.name}</span>
                    <span className="text-[#7a7a7a] text-xs mt-0.5 flex-1">— {p.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {d && (
        <div className={`rounded-2xl border px-5 py-4 mb-6 flex items-center gap-3 ${banner.cls}`} data-testid="readiness-banner">
          <banner.Icon size={24} weight="fill" />
          <div className="flex-1 text-sm font-semibold">{banner.msg}</div>
          <div className="text-xs opacity-90">🟢 {d.greens} · 🟡 {d.yellows} · 🔴 {d.reds}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="readiness-checks">
        {(d?.checks || []).map((c) => (
          <div key={c.key} className="card-matte p-4 flex items-start gap-3" data-testid={`readiness-${c.key}`}>
            <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${DOT[c.color]}`} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-head font-semibold text-sm">{c.name}</span>
                <span className={`text-[10px] font-semibold uppercase ${TXT[c.color]}`}>{c.color === "green" ? "OK" : c.color === "yellow" ? "Warning" : "Failed"}</span>
              </div>
              <div className="text-xs text-[#9a9a9a] mt-0.5">{c.detail}</div>
            </div>
          </div>
        ))}
        {!d && <div className="col-span-2 text-center text-[#7a7a7a] py-16">Running system diagnostics…</div>}
      </div>
    </PageContainer>
  );
}
